import { useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import CaseCard from "@/components/feature/CaseCard";
import CaseModal from "@/components/feature/CaseModal";
import { caseItems, caseCategories, type CaseItem } from "@/mocks/lawfirm";
import CasesHero from "./components/CasesHero";

const PER_PAGE = 9;
const filters = ["전체", ...caseCategories];

export default function CasesPage() {
  const [active, setActive] = useState("전체");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<CaseItem | null>(null);

  const filtered = useMemo(
    () => (active === "전체" ? caseItems : caseItems.filter((c) => c.category === active)),
    [active]
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const list = filtered.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  const selectFilter = (f: string) => {
    setActive(f);
    setPage(1);
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <CasesHero />

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => selectFilter(f)}
                  className={`rounded-full px-5 py-2 text-xs md:text-sm font-medium transition-colors whitespace-nowrap ${
                    active === f
                      ? "bg-primary-500 text-white"
                      : "bg-white text-foreground-700 border border-background-200 hover:border-primary-300"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {list.map((c) => (
                <CaseCard key={c.id} item={c} onClick={setSelected} />
              ))}
            </div>

            {filtered.length === 0 && (
              <p className="mt-16 text-center text-foreground-500">등록된 사례가 없습니다.</p>
            )}

            {totalPages > 1 && (
              <div className="mt-12 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={current === 1}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 bg-white text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line text-lg"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors ${
                      current === n
                        ? "bg-primary-500 text-white"
                        : "bg-white text-foreground-700 border border-background-200 hover:border-primary-300"
                    }`}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={current === totalPages}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 bg-white text-foreground-700 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </button>
              </div>
            )}

            <p className="mt-8 text-center text-xs text-foreground-400">
              ※ 사례는 의뢰인 정보 보호를 위해 요약된 내용이며, 사건의 특성에 따라 결과가 달라질 수 있습니다.
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <CaseModal item={selected} onClose={() => setSelected(null)} />
    </>
  );
}